import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar as CalendarIcon, ChevronRight, Clock } from "lucide-react";
import api from "../../lib/api";
import { useT } from "../../context/LanguageContext";

const fmtTime = (date, locale) =>
  date.toLocaleTimeString(locale === "es" ? "es-ES" : "en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

export const TodayJobsWidget = () => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/calendar/today");
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !data) return null;

  const { today_count: count = 0, upcoming_count: upcoming = 0, events = [] } = data;

  return (
    <div
      className="rounded-2xl bg-white border border-gray-200 shadow-sm p-5"
      data-testid="today-jobs-widget"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-green-100 text-green-800 flex items-center justify-center">
            <CalendarIcon className="w-4 h-4" />
          </span>
          <div>
            <p className="font-display font-semibold text-gray-900" data-testid="today-jobs-title">
              {t("todayJobs.title")}
            </p>
            <p className="text-xs text-gray-500" data-testid="today-jobs-summary">
              {count === 0
                ? t("todayJobs.emptyTitle")
                : count === 1
                  ? t("todayJobs.countOne")
                  : t("todayJobs.countMany", count)}
              {upcoming > 0 && count > 0 && ` · ${t("todayJobs.upcomingToday", upcoming)}`}
              {count > 0 && upcoming === 0 && ` · ${t("todayJobs.completedToday")}`}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/calendar")}
          className="text-xs font-semibold text-green-800 hover:text-green-900 flex items-center gap-0.5 self-center"
          data-testid="today-jobs-open-calendar"
        >
          {t("todayJobs.viewCalendar")}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {count === 0 ? (
        <p className="text-sm text-gray-500" data-testid="today-jobs-empty-body">
          {t("todayJobs.emptyBody")}
        </p>
      ) : (
        <div className="space-y-2" data-testid="today-jobs-list">
          {events.slice(0, 4).map((e) => {
            const hasTime = !!e.scheduled_time_set;
            const dt = hasTime && e.scheduled_at ? new Date(e.scheduled_at) : null;
            const ds = e.display_status || e.status;
            const dotColor =
              ds === "completed" ? "bg-sky-500" : ds === "cancelled" ? "bg-red-500" : "bg-green-600";
            return (
              <button
                key={e.id}
                type="button"
                onClick={() => navigate("/calendar")}
                className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-xl border border-gray-100 hover:border-green-300 hover:bg-green-50/50 transition"
                data-testid={`today-jobs-item-${e.id}`}
              >
                <span className={`w-2 h-2 rounded-full ${dotColor} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {e.customer_name || "Customer"}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {e.service_type || ""}
                    {e.service_type && e.address ? " · " : ""}
                    {e.address || ""}
                  </p>
                </div>
                {dt ? (
                  <span className="text-xs font-semibold text-gray-700 flex items-center gap-1 shrink-0">
                    <Clock className="w-3 h-3 text-gray-400" />
                    {fmtTime(dt, lang)}
                  </span>
                ) : (
                  <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 shrink-0">
                    {t("cal.timeNotSet")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
